const sectionTitleStyle = {
  fontFamily: "var(--font-serif)",
  fontStyle: "italic",
  fontSize: "18px",
  color: "var(--color-ink)",
  margin: 0,
};

const bodyStyle = { fontSize: "16px", lineHeight: 1.7, color: "rgba(10,9,7,0.85)", margin: 0 };
const listStyle = { display: "flex", flexDirection: "column", gap: "10px", margin: 0, paddingLeft: "20px" };

function Section({ title, children }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
      <p style={sectionTitleStyle}>{title}</p>
      {children}
    </div>
  );
}

/**
 * Based on konzepte/datenschutzerklaerung.html — a generic DSGVO-generator
 * template. Kept: the mandatory legal boilerplate (Begriffsbestimmungen,
 * Betroffenenrechte, Rechtsgrundlagen, Speicherdauer, §21–25) and every
 * section describing something this codebase actually does (Cookies,
 * Server-Logfiles, Newsletter via EmailOctopus, Kontaktformular, Google
 * Analytics/GA4, Cloudflare Turnstile, PayPal, and the new ACS/Azure
 * section this template didn't have). Dropped entirely: Facebook, Google
 * AdSense, Google AdWords/conversion-cookies, Instagram-as-embedded-
 * component (the footer/Info-page Instagram link is a plain hyperlink,
 * not an embedded widget that transmits visitor data — doesn't need its
 * own GDPR section), LinkedIn, YouTube, blog-comment subscriptions, and
 * the Bewerbungsverfahren (job-application) section — none of these exist
 * on this site, and including them would claim data processing that
 * doesn't happen. The Google Analytics section was also rewritten: the
 * template described old Universal Analytics (`_gat._anonymizeIp` is a UA
 * concept); this site uses GA4 (`react-ga4`), which anonymizes IPs by
 * default with no separate flag.
 *
 * German is the legally binding version; DatenschutzEn is a courtesy
 * translation (same convention as AgbContent.jsx) — keep both in sync
 * section-for-section if either changes.
 */
export function DatenschutzDe() {
  return (
    <div className="tl-card-light" style={{ display: "flex", flexDirection: "column", gap: "28px", padding: "44px 40px" }}>
      <p style={bodyStyle}>
        Wir freuen uns sehr über Ihr Interesse an unserer Arbeit. Datenschutz hat einen besonders hohen Stellenwert
        für Tales&amp;Legends gemeinnützige UG (haftungsbeschränkt). Eine Nutzung unserer Internetseite ist
        grundsätzlich ohne jede Angabe personenbezogener Daten möglich. Sofern eine betroffene Person besondere
        Services (z. B. Spenden, Newsletter, Kontaktaufnahme) über unsere Internetseite in Anspruch nehmen möchte,
        kann jedoch eine Verarbeitung personenbezogener Daten erforderlich werden. Ist die Verarbeitung
        personenbezogener Daten erforderlich und besteht für eine solche Verarbeitung keine gesetzliche Grundlage,
        holen wir generell eine Einwilligung der betroffenen Person ein.
      </p>
      <p style={bodyStyle}>
        Die Verarbeitung personenbezogener Daten erfolgt stets im Einklang mit der Datenschutz-Grundverordnung (DSGVO)
        und den für uns geltenden landesspezifischen Datenschutzbestimmungen. Mittels dieser Datenschutzerklärung
        informieren wir über Art, Umfang und Zweck der von uns erhobenen, genutzten und verarbeiteten
        personenbezogenen Daten sowie über die Ihnen zustehenden Rechte.
      </p>

      <Section title="1. Begriffsbestimmungen">
        <p style={bodyStyle}>
          Diese Datenschutzerklärung beruht auf den Begrifflichkeiten der DSGVO. Wir verwenden unter anderem die
          folgenden Begriffe:
        </p>
        <ul style={listStyle}>
          <li style={bodyStyle}>
            <strong>Personenbezogene Daten:</strong> alle Informationen, die sich auf eine identifizierte oder
            identifizierbare natürliche Person („betroffene Person") beziehen.
          </li>
          <li style={bodyStyle}>
            <strong>Verarbeitung:</strong> jeder mit oder ohne Hilfe automatisierter Verfahren ausgeführte Vorgang im
            Zusammenhang mit personenbezogenen Daten (Erheben, Erfassen, Speichern, Verändern, Auslesen, Verwenden,
            Offenlegen, Löschen etc.).
          </li>
          <li style={bodyStyle}>
            <strong>Einschränkung der Verarbeitung:</strong> die Markierung gespeicherter personenbezogener Daten mit
            dem Ziel, ihre künftige Verarbeitung einzuschränken.
          </li>
          <li style={bodyStyle}>
            <strong>Pseudonymisierung:</strong> die Verarbeitung personenbezogener Daten in einer Weise, auf welche
            die Daten ohne Hinzuziehung zusätzlicher, gesondert aufbewahrter Informationen nicht mehr einer
            bestimmten betroffenen Person zugeordnet werden können.
          </li>
          <li style={bodyStyle}>
            <strong>Verantwortlicher:</strong> die natürliche oder juristische Person, die allein oder gemeinsam mit
            anderen über die Zwecke und Mittel der Verarbeitung personenbezogener Daten entscheidet.
          </li>
          <li style={bodyStyle}>
            <strong>Auftragsverarbeiter:</strong> eine natürliche oder juristische Person, die personenbezogene Daten
            im Auftrag des Verantwortlichen verarbeitet.
          </li>
          <li style={bodyStyle}>
            <strong>Einwilligung:</strong> jede freiwillig, informiert und unmissverständlich abgegebene
            Willensbekundung, mit der die betroffene Person zu verstehen gibt, dass sie mit der Verarbeitung der sie
            betreffenden personenbezogenen Daten einverstanden ist.
          </li>
        </ul>
      </Section>

      <Section title="2. Name und Anschrift des Verantwortlichen">
        <p style={bodyStyle}>
          Verantwortlicher im Sinne der DSGVO ist:
          <br />
          Tales&amp;Legends gemeinnützige UG (haftungsbeschränkt)
          <br />
          Reiboldschachtring 28
          <br />
          01705 Freital
          <br />
          Deutschland
          <br />
          Tel.: 01522 2584587
          <br />
          E-Mail: <a href="mailto:nils@talesandlegends.org">nils@talesandlegends.org</a>
          <br />
          Website: <a href="https://talesandlegends.org">https://talesandlegends.org</a>
        </p>
      </Section>

      <Section title="3. Cookies">
        <p style={bodyStyle}>
          Unsere Internetseite verwendet Cookies — Textdateien, die über den Internetbrowser auf dem Computersystem
          der betroffenen Person abgelegt und gespeichert werden. Cookies ermöglichen es uns unter anderem, die
          Internetseite nutzerfreundlicher zu gestalten und wiederkehrende Besucher wiederzuerkennen.
        </p>
        <p style={bodyStyle}>
          Nicht technisch notwendige Cookies (z. B. für Google Analytics) setzen wir erst, nachdem die betroffene
          Person dem über den Cookie-Consent-Banner unserer Internetseite zugestimmt hat. Die betroffene Person kann
          die Setzung von Cookies jederzeit über die Einstellungen des genutzten Internetbrowsers verhindern und
          bereits gesetzte Cookies löschen. Deaktiviert die betroffene Person Cookies, sind unter Umständen nicht
          alle Funktionen unserer Internetseite vollumfänglich nutzbar.
        </p>
      </Section>

      <Section title="4. Erfassung allgemeiner Daten und Informationen (Server-Logfiles)">
        <p style={bodyStyle}>
          Unsere Internetseite erfasst mit jedem Aufruf eine Reihe allgemeiner Daten und Informationen in den
          Logfiles des Servers: verwendeter Browsertyp und -version, Betriebssystem, Referrer-URL, aufgerufene
          Unterseiten, Datum und Uhrzeit des Zugriffs, IP-Adresse sowie der Internet-Service-Provider des
          zugreifenden Systems.
        </p>
        <p style={bodyStyle}>
          Wir ziehen aus diesen Daten keine Rückschlüsse auf die betroffene Person. Sie werden benötigt, um die
          Inhalte unserer Internetseite korrekt auszuliefern, die dauerhafte Funktionsfähigkeit unserer
          informationstechnologischen Systeme zu gewährleisten und um im Falle eines Cyberangriffs die zur
          Strafverfolgung notwendigen Informationen bereitstellen zu können.
        </p>
      </Section>

      <Section title="5. Newsletter">
        <p style={bodyStyle}>
          Auf unserer Internetseite besteht die Möglichkeit, unseren Newsletter zu abonnieren. Welche
          personenbezogenen Daten dabei übermittelt werden, ergibt sich aus der hierzu verwendeten Eingabemaske. Wir
          nutzen für Versand und Verwaltung des Newsletters den Dienst EmailOctopus (EmailOctopus Ltd.) als
          Auftragsverarbeiter.
        </p>
        <p style={bodyStyle}>
          Der Versand erfolgt im Double-Opt-In-Verfahren: An die erstmalig eingetragene E-Mail-Adresse wird eine
          Bestätigungsmail versendet, die überprüft, ob die betroffene Person selbst den Empfang des Newsletters
          autorisiert hat. Zusätzlich speichern wir die vom Internet-Service-Provider vergebene IP-Adresse sowie
          Datum und Uhrzeit der Anmeldung, um einen möglichen späteren Missbrauch der E-Mail-Adresse nachvollziehen
          zu können.
        </p>
        <p style={bodyStyle}>
          Die im Rahmen der Anmeldung erhobenen Daten werden ausschließlich zum Versand des Newsletters verwendet und
          nicht an Dritte außerhalb von EmailOctopus weitergegeben. Das Abonnement kann jederzeit über den Abmeldelink
          in jedem Newsletter oder durch Nachricht an uns beendet werden.
        </p>
      </Section>

      <Section title="6. Newsletter-Tracking">
        <p style={bodyStyle}>
          Unsere über EmailOctopus versendeten Newsletter können sogenannte Zählpixel enthalten — Miniaturgrafiken,
          die eine Logdatei-Aufzeichnung und -Analyse ermöglichen. Dadurch können wir statistisch auswerten, ob und
          wann eine E-Mail geöffnet und welche darin enthaltenen Links aufgerufen wurden, um den Newsletterversand zu
          optimieren. Diese Daten werden nicht an Dritte außerhalb von EmailOctopus weitergegeben. Eine Abmeldung vom
          Newsletter werten wir automatisch als Widerruf dieser Auswertung.
        </p>
      </Section>

      <Section title="7. Kontaktmöglichkeit über die Internetseite">
        <p style={bodyStyle}>
          Unsere Internetseite enthält aufgrund gesetzlicher Vorschriften Angaben, die eine schnelle elektronische
          Kontaktaufnahme sowie eine unmittelbare Kommunikation mit uns ermöglichen, einschließlich einer
          E-Mail-Adresse. Sofern eine betroffene Person per E-Mail oder über unser Kontaktformular Kontakt mit uns
          aufnimmt, werden die übermittelten personenbezogenen Daten automatisch gespeichert und ausschließlich zur
          Bearbeitung der Anfrage bzw. zur Kontaktaufnahme verwendet. Es erfolgt keine Weitergabe dieser Daten an
          Dritte.
        </p>
      </Section>

      <Section title="8. Routinemäßige Löschung und Sperrung personenbezogener Daten">
        <p style={bodyStyle}>
          Wir verarbeiten und speichern personenbezogene Daten nur für den Zeitraum, der zur Erreichung des
          Speicherungszwecks erforderlich ist, oder sofern dies gesetzlich vorgesehen ist. Entfällt der
          Speicherungszweck oder läuft eine gesetzlich vorgeschriebene Speicherfrist ab, werden die Daten
          routinemäßig gesperrt oder gelöscht.
        </p>
      </Section>

      <Section title="9. Rechte der betroffenen Person">
        <ul style={listStyle}>
          <li style={bodyStyle}>
            <strong>Recht auf Bestätigung und Auskunft:</strong> Sie können jederzeit unentgeltlich Auskunft über die
            zu Ihrer Person gespeicherten personenbezogenen Daten sowie eine Kopie dieser Daten verlangen,
            einschließlich Informationen zu Verarbeitungszwecken, Kategorien der Daten, Empfängern und der geplanten
            Speicherdauer.
          </li>
          <li style={bodyStyle}>
            <strong>Recht auf Berichtigung:</strong> Sie können die unverzügliche Berichtigung unrichtiger oder die
            Vervollständigung unvollständiger personenbezogener Daten verlangen.
          </li>
          <li style={bodyStyle}>
            <strong>Recht auf Löschung („Recht auf Vergessenwerden"):</strong> Sie können die Löschung Ihrer
            personenbezogenen Daten verlangen, sofern einer der in Art. 17 DSGVO genannten Gründe zutrifft (z. B.
            Zweckfortfall, Widerruf der Einwilligung, unrechtmäßige Verarbeitung).
          </li>
          <li style={bodyStyle}>
            <strong>Recht auf Einschränkung der Verarbeitung:</strong> Sie können die Einschränkung der Verarbeitung
            verlangen, u. a. wenn Sie die Richtigkeit der Daten bestreiten oder der Löschung widersprechen und
            stattdessen die Einschränkung der Nutzung verlangen.
          </li>
          <li style={bodyStyle}>
            <strong>Recht auf Datenübertragbarkeit:</strong> Sie können die Sie betreffenden, uns bereitgestellten
            personenbezogenen Daten in einem strukturierten, gängigen, maschinenlesbaren Format erhalten und einem
            anderen Verantwortlichen übermitteln lassen, sofern die Verarbeitung auf einer Einwilligung oder einem
            Vertrag beruht und automatisiert erfolgt.
          </li>
          <li style={bodyStyle}>
            <strong>Recht auf Widerspruch:</strong> Sie können aus Gründen, die sich aus Ihrer besonderen Situation
            ergeben, jederzeit gegen die auf Art. 6 Abs. 1 lit. e oder f DSGVO gestützte Verarbeitung Ihrer
            personenbezogenen Daten Widerspruch einlegen; dies gilt auch für ein darauf gestütztes Profiling.
          </li>
          <li style={bodyStyle}>
            <strong>Automatisierte Entscheidungen im Einzelfall:</strong> Sie haben das Recht, nicht einer
            ausschließlich auf automatisierter Verarbeitung — einschließlich Profiling — beruhenden Entscheidung
            unterworfen zu werden, die Ihnen gegenüber rechtliche Wirkung entfaltet oder Sie in ähnlicher Weise
            erheblich beeinträchtigt.
          </li>
          <li style={bodyStyle}>
            <strong>Recht auf Widerruf einer datenschutzrechtlichen Einwilligung:</strong> Sie können eine erteilte
            Einwilligung jederzeit mit Wirkung für die Zukunft widerrufen.
          </li>
          <li style={bodyStyle}>
            <strong>Recht auf Beschwerde bei einer Aufsichtsbehörde:</strong> Unbeschadet eines anderweitigen
            verwaltungsrechtlichen oder gerichtlichen Rechtsbehelfs steht Ihnen das Recht auf Beschwerde bei einer
            Datenschutz-Aufsichtsbehörde zu, insbesondere in dem Mitgliedstaat Ihres Aufenthaltsorts, Ihres
            Arbeitsplatzes oder des Orts des mutmaßlichen Verstoßes.
          </li>
        </ul>
        <p style={bodyStyle}>
          Zur Ausübung dieser Rechte können Sie sich jederzeit an die in Ziffer 2 genannten Kontaktdaten wenden.
        </p>
      </Section>

      <Section title="10. Google Analytics (GA4)">
        <p style={bodyStyle}>
          Wir setzen auf unserer Internetseite Google Analytics 4 (GA4) ein, einen Webanalysedienst der Google
          Ireland Limited, Gordon House, Barrow Street, Dublin, D04 E5W5, Irland. GA4 erfasst unter anderem, von
          welcher Seite eine betroffene Person zu uns gelangt ist (Referrer), welche Unterseiten aufgerufen und wie
          lange sie betrachtet wurden, um unsere Internetseite zu optimieren.
        </p>
        <p style={bodyStyle}>
          GA4 kürzt und anonymisiert IP-Adressen standardmäßig und ohne gesonderte Konfiguration, bevor sie
          gespeichert werden — anders als bei älteren Google-Analytics-Versionen ist hierfür kein zusätzliches Merkmal
          erforderlich. GA4 wird erst geladen, nachdem die betroffene Person über unseren Cookie-Consent-Banner
          zugestimmt hat.
        </p>
        <p style={bodyStyle}>
          Die betroffene Person kann der Erfassung durch GA4 widersprechen, indem sie im Consent-Banner ablehnt oder
          das von Google bereitgestellte Browser-Add-On unter{" "}
          <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer">
            https://tools.google.com/dlpage/gaoptout
          </a>{" "}
          installiert. Weitere Informationen zu den Datenschutzbestimmungen von Google finden sich unter{" "}
          <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">
            https://policies.google.com/privacy
          </a>
          .
        </p>
      </Section>

      <Section title="11. Cloudflare Turnstile">
        <p style={bodyStyle}>
          Wir setzen auf unserer Internetseite den Dienst Cloudflare Turnstile ein, um Formulare (Spenden, Kontakt)
          vor automatisierten Zugriffen (Bots) zu schützen — als datenschutzfreundliche Alternative zu klassischen
          CAPTCHAs, die in der Regel ohne Lösung einer Aufgabe auskommt.
        </p>
        <p style={bodyStyle}>
          Betreibergesellschaft ist die Cloudflare, Inc., 101 Townsend St., San Francisco, CA 94107, USA; für Nutzer
          im Europäischen Wirtschaftsraum ergänzend die Cloudflare Germany GmbH, Rosental 7, c/o Mindspace, 80331
          München. Bei Einbindung von Turnstile können IP-Adresse, Informationen zu Browser und Betriebssystem, die
          aufgerufene Unterseite, Datum/Uhrzeit sowie Interaktionsdaten des Prüfvorgangs an Cloudflare übermittelt
          werden; eine Speicherung in einem Cookie kann erfolgen.
        </p>
        <p style={bodyStyle}>
          Die Verarbeitung erfolgt auf Grundlage von Art. 6 Abs. 1 lit. f DSGVO — unser berechtigtes Interesse ist der
          Schutz unserer Formulare vor Missbrauch und Spam. Die Übermittlung in die USA erfolgt auf Grundlage der von
          der Europäischen Kommission genehmigten Standardvertragsklauseln; Cloudflare hat sich zudem dem EU-U.S.
          Data Privacy Framework angeschlossen. Die Datenschutzbestimmungen von Cloudflare sind unter{" "}
          <a href="https://www.cloudflare.com/de-de/privacypolicy/" target="_blank" rel="noopener noreferrer">
            https://www.cloudflare.com/de-de/privacypolicy/
          </a>{" "}
          abrufbar, das Turnstile Privacy Addendum unter{" "}
          <a href="https://www.cloudflare.com/turnstile-privacy-policy/" target="_blank" rel="noopener noreferrer">
            https://www.cloudflare.com/turnstile-privacy-policy/
          </a>
          .
        </p>
      </Section>

      <Section title="12. PayPal als Zahlungsart">
        <p style={bodyStyle}>
          Für Spenden bieten wir PayPal als Zahlungsmöglichkeit an. Europäische Betreibergesellschaft ist die PayPal
          (Europe) S.à.r.l. et Cie, S.C.A., 22-24 Boulevard Royal, L-2449 Luxemburg. Wählt die betroffene Person
          PayPal als Zahlungsweg, werden die zur Zahlungsabwicklung erforderlichen Daten (z. B. Name, Adresse,
          E-Mail-Adresse, IP-Adresse) automatisiert an PayPal übermittelt. Mit der Auswahl dieser Zahlungsoption
          willigt die betroffene Person in diese Übermittlung ein.
        </p>
        <p style={bodyStyle}>
          Die Übermittlung dient der Zahlungsabwicklung und Betrugsprävention. Ein Widerruf der Einwilligung
          gegenüber PayPal wirkt sich nicht auf bereits zur vertragsgemäßen Zahlungsabwicklung verarbeitete Daten aus.
          Die Datenschutzbestimmungen von PayPal sind unter{" "}
          <a href="https://www.paypal.com/de/webapps/mpp/ua/privacy-full" target="_blank" rel="noopener noreferrer">
            https://www.paypal.com/de/webapps/mpp/ua/privacy-full
          </a>{" "}
          abrufbar.
        </p>
      </Section>

      <Section title="13. Azure Communication Services (E-Mail-Versand)">
        <p style={bodyStyle}>
          Automatisierte E-Mails (Spendenbestätigung, Spendenbescheinigung, Zahlungserinnerung, Weiterleitung von
          Kontaktformular-Anfragen) versenden wir über Azure Communication Services, einen Dienst der Microsoft
          Ireland Operations Ltd., One Microsoft Place, South County Business Park, Leopardstown, Dublin 18, Irland.
          Verarbeitet werden dabei die für den jeweiligen E-Mail-Versand notwendigen Daten (u. a. E-Mail-Adresse,
          Name, Betreff- und Inhaltsdaten der jeweiligen Nachricht).
        </p>
        <p style={bodyStyle}>
          Rechtsgrundlage ist Art. 6 Abs. 1 lit. b DSGVO (Erfüllung des Spenden- bzw. Vertragsverhältnisses) bzw. Art.
          6 Abs. 1 lit. f DSGVO (berechtigtes Interesse an der Beantwortung von Kontaktanfragen). Mit Microsoft
          besteht ein Auftragsverarbeitungsvertrag. Weitere Informationen zu den Datenschutzbestimmungen von
          Microsoft Azure sind unter{" "}
          <a href="https://privacy.microsoft.com/de-de/privacystatement" target="_blank" rel="noopener noreferrer">
            https://privacy.microsoft.com/de-de/privacystatement
          </a>{" "}
          abrufbar.
        </p>
      </Section>

      <Section title="14. Speicherung von Spenden- und Kontaktdaten">
        <p style={bodyStyle}>
          Zur Abwicklung von Spenden (inkl. Spendenbescheinigung) und zur Bearbeitung von Kontaktanfragen speichern
          wir personenbezogene Daten in einer Microsoft-Azure-Datenbank (Cosmos DB) sowie erzeugte
          Spendenbescheinigungen als PDF in einem Microsoft-Azure-Speicherkonto (Blob Storage). Auch hierfür gilt ein
          Auftragsverarbeitungsvertrag mit Microsoft; die Daten werden nur so lange gespeichert, wie es für die
          Abwicklung der Spende und die gesetzlichen Aufbewahrungspflichten (insbesondere steuerliche
          Aufbewahrungsfristen für Spendenbescheinigungen) erforderlich ist.
        </p>
      </Section>

      <Section title="15. Rechtsgrundlage der Verarbeitung">
        <p style={bodyStyle}>
          Soweit wir eine Einwilligung für einen bestimmten Verarbeitungszweck einholen, dient uns Art. 6 Abs. 1 lit.
          a DSGVO als Rechtsgrundlage. Ist die Verarbeitung zur Erfüllung eines Vertrags erforderlich, dessen
          Vertragspartei die betroffene Person ist (z. B. Abwicklung einer Spende), beruht die Verarbeitung auf Art.
          6 Abs. 1 lit. b DSGVO; Gleiches gilt für Verarbeitungen zur Durchführung vorvertraglicher Maßnahmen.
          Unterliegen wir einer rechtlichen Verpflichtung, die eine Verarbeitung erforderlich macht (z. B. steuerliche
          Aufbewahrungspflichten), beruht die Verarbeitung auf Art. 6 Abs. 1 lit. c DSGVO. Im Übrigen können
          Verarbeitungsvorgänge auf Art. 6 Abs. 1 lit. f DSGVO beruhen, sofern sie zur Wahrung eines berechtigten
          Interesses erforderlich sind und die Interessen, Grundrechte und Grundfreiheiten der betroffenen Person
          nicht überwiegen.
        </p>
      </Section>

      <Section title="16. Berechtigte Interessen an der Verarbeitung">
        <p style={bodyStyle}>
          Soweit sich die Verarbeitung auf Art. 6 Abs. 1 lit. f DSGVO stützt, besteht unser berechtigtes Interesse in
          der Erfüllung unseres gemeinnützigen Zwecks — der Durchführung und Absicherung unserer LARP-Veranstaltungen,
          der Abwicklung von Spenden sowie der sicheren und funktionsfähigen Bereitstellung unserer Internetseite.
        </p>
      </Section>

      <Section title="17. Speicherdauer">
        <p style={bodyStyle}>
          Maßgeblich für die Dauer der Speicherung personenbezogener Daten ist die jeweilige gesetzliche
          Aufbewahrungsfrist (insbesondere handels- und steuerrechtliche Fristen für Spendenbescheinigungen). Nach
          Ablauf der Frist werden die Daten routinemäßig gelöscht, sofern sie nicht mehr zur Vertragserfüllung oder
          -anbahnung erforderlich sind.
        </p>
      </Section>

      <Section title="18. Bereitstellung personenbezogener Daten">
        <p style={bodyStyle}>
          Wir weisen darauf hin, dass die Bereitstellung personenbezogener Daten zum Teil gesetzlich vorgeschrieben
          ist (z. B. steuerliche Vorschriften bei Spendenbescheinigungen) oder sich aus vertraglichen Regelungen
          ergeben kann. Ohne bestimmte Daten (z. B. eine gültige E-Mail-Adresse) kann eine Spende, eine
          Newsletter-Anmeldung oder die Bearbeitung einer Kontaktanfrage unter Umständen nicht durchgeführt werden.
        </p>
      </Section>

      <Section title="19. Automatisierte Entscheidungsfindung">
        <p style={bodyStyle}>Wir verzichten auf eine automatisierte Entscheidungsfindung oder ein Profiling.</p>
      </Section>
    </div>
  );
}

/** Courtesy translation — German is the legally binding version (see DatenschutzDe). */
export function DatenschutzEn() {
  return (
    <div className="tl-card-light" style={{ display: "flex", flexDirection: "column", gap: "28px", padding: "44px 40px" }}>
      <p style={{ ...bodyStyle, fontSize: "13px", color: "rgba(10,9,7,0.6)" }}>
        Courtesy translation. The German version is the legally binding one.
      </p>
      <p style={bodyStyle}>
        We are delighted by your interest in our work. Data protection is a particularly high priority for
        Tales&amp;Legends gemeinnützige UG (haftungsbeschränkt). Our website can generally be used without providing
        any personal data. However, if a data subject wishes to use special services offered through our website,
        processing of personal data may become necessary. Where such processing is necessary and there is no
        statutory basis for it, we generally obtain the data subject's consent.
      </p>
      <p style={bodyStyle}>
        The processing of personal data is always carried out in compliance with the General Data Protection
        Regulation (GDPR) and the country-specific data protection provisions applicable to us. Through this privacy
        policy, we inform the public about the nature, scope, and purpose of the personal data we collect, use, and
        process, and about the rights to which data subjects are entitled.
      </p>

      <Section title="1. Definitions">
        <p style={bodyStyle}>This privacy policy is based on the terminology used in the GDPR. We use, among others, the following terms:</p>
        <ul style={listStyle}>
          <li style={bodyStyle}>
            <strong>Personal data:</strong> any information relating to an identified or identifiable natural person
            (the "data subject").
          </li>
          <li style={bodyStyle}>
            <strong>Processing:</strong> any operation performed on personal data, whether or not by automated means
            (collection, recording, storage, alteration, retrieval, use, disclosure, erasure, etc.).
          </li>
          <li style={bodyStyle}>
            <strong>Restriction of processing:</strong> the marking of stored personal data with the aim of limiting
            its processing in the future.
          </li>
          <li style={bodyStyle}>
            <strong>Pseudonymization:</strong> the processing of personal data in such a way that it can no longer be
            attributed to a specific data subject without the use of additional, separately held information.
          </li>
          <li style={bodyStyle}>
            <strong>Controller:</strong> the natural or legal person which, alone or jointly with others, determines
            the purposes and means of the processing of personal data.
          </li>
          <li style={bodyStyle}>
            <strong>Processor:</strong> a natural or legal person which processes personal data on behalf of the
            controller.
          </li>
          <li style={bodyStyle}>
            <strong>Consent:</strong> any freely given, informed, and unambiguous indication of the data subject's
            wishes by which they signify agreement to the processing of personal data relating to them.
          </li>
        </ul>
      </Section>

      <Section title="2. Name and address of the controller">
        <p style={bodyStyle}>
          The controller within the meaning of the GDPR is:
          <br />
          Tales&amp;Legends gemeinnützige UG (haftungsbeschränkt)
          <br />
          Reiboldschachtring 28
          <br />
          01705 Freital
          <br />
          Germany
          <br />
          Phone: 01522 2584587
          <br />
          Email: <a href="mailto:nils@talesandlegends.org">nils@talesandlegends.org</a>
          <br />
          Website: <a href="https://talesandlegends.org">https://talesandlegends.org</a>
        </p>
      </Section>

      <Section title="3. Cookies">
        <p style={bodyStyle}>
          Our website uses cookies — text files stored on the data subject's computer system via their browser.
          Cookies help us make our website more user-friendly and recognize returning visitors.
        </p>
        <p style={bodyStyle}>
          Non-essential cookies (e.g., for Google Analytics) are only set once the data subject has given consent via
          our website's cookie-consent banner. Data subjects can prevent cookies from being set at any time via their
          browser settings and can delete cookies already stored. Disabling cookies may mean not all features of our
          website are fully usable.
        </p>
      </Section>

      <Section title="4. Collection of general data and information (server log files)">
        <p style={bodyStyle}>
          Our website collects a range of general data and information with every access, stored in the server's log
          files: browser type and version used, operating system, referrer URL, subpages accessed, date and time of
          access, IP address, and the internet service provider of the accessing system.
        </p>
        <p style={bodyStyle}>
          We draw no conclusions about the data subject from this data. It is needed to correctly deliver the
          content of our website, ensure the continued functionality of our IT systems, and to provide law
          enforcement authorities with the information necessary for prosecution in the event of a cyberattack.
        </p>
      </Section>

      <Section title="5. Newsletter">
        <p style={bodyStyle}>
          Our website offers the option to subscribe to our newsletter. Which personal data is transmitted when
          ordering the newsletter is apparent from the input form used for this purpose. We use EmailOctopus
          (EmailOctopus Ltd.) as a processor for sending and managing the newsletter.
        </p>
        <p style={bodyStyle}>
          Sending follows the double opt-in procedure: a confirmation email is sent to the email address entered for
          the first time, verifying that the holder of that address has authorized receipt of the newsletter. We
          also store the IP address assigned by the internet service provider and the date and time of registration,
          to be able to trace any possible later misuse of the email address.
        </p>
        <p style={bodyStyle}>
          Data collected as part of newsletter registration is used exclusively to send the newsletter and is not
          passed on to third parties outside of EmailOctopus. The subscription can be cancelled at any time via the
          unsubscribe link in every newsletter or by contacting us.
        </p>
      </Section>

      <Section title="6. Newsletter tracking">
        <p style={bodyStyle}>
          Our newsletters, sent via EmailOctopus, may contain what are known as tracking pixels — miniature graphics
          embedded in the email that enable a log file to be recorded and analyzed. This allows us to determine
          statistically whether and when an email was opened and which links it contains were clicked, in order to
          optimize our newsletter distribution. This data is not passed on to third parties outside of EmailOctopus.
          We treat an unsubscribe from the newsletter as an automatic withdrawal of this consent.
        </p>
      </Section>

      <Section title="7. Contact options via the website">
        <p style={bodyStyle}>
          Due to legal requirements, our website contains information that enables quick electronic contact with us
          and direct communication, including a general email address. If a data subject contacts us by email or via
          our contact form, the personal data transmitted is automatically stored and used exclusively to process
          the inquiry or to make contact with the data subject. This personal data is not passed on to third parties.
        </p>
      </Section>

      <Section title="8. Routine erasure and blocking of personal data">
        <p style={bodyStyle}>
          We process and store personal data only for the period necessary to achieve the storage purpose, or as
          provided for by law. If the storage purpose no longer applies, or a statutory retention period expires,
          the personal data is routinely blocked or erased in accordance with statutory provisions.
        </p>
      </Section>

      <Section title="9. Rights of the data subject">
        <ul style={listStyle}>
          <li style={bodyStyle}>
            <strong>Right of confirmation and access:</strong> you may, at any time, request free confirmation and
            information about the personal data stored about you, including a copy of that data, as well as
            information about processing purposes, categories of data, recipients, and the intended storage period.
          </li>
          <li style={bodyStyle}>
            <strong>Right to rectification:</strong> you may request the immediate correction of inaccurate personal
            data, or the completion of incomplete personal data.
          </li>
          <li style={bodyStyle}>
            <strong>Right to erasure ("right to be forgotten"):</strong> you may request the erasure of your personal
            data where one of the grounds listed in Art. 17 GDPR applies (e.g., the data is no longer necessary,
            consent is withdrawn, or the data was processed unlawfully).
          </li>
          <li style={bodyStyle}>
            <strong>Right to restriction of processing:</strong> you may request restriction of processing, e.g. if
            you contest the accuracy of the data or oppose erasure and request restricted use instead.
          </li>
          <li style={bodyStyle}>
            <strong>Right to data portability:</strong> you may receive personal data concerning you that you have
            provided to us in a structured, commonly used, machine-readable format, and have it transmitted to
            another controller, provided the processing is based on consent or a contract and is carried out by
            automated means.
          </li>
          <li style={bodyStyle}>
            <strong>Right to object:</strong> on grounds relating to your particular situation, you may object at any
            time to processing of your personal data based on Art. 6(1)(e) or (f) GDPR; this also applies to
            profiling based on these provisions.
          </li>
          <li style={bodyStyle}>
            <strong>Automated individual decision-making:</strong> you have the right not to be subject to a decision
            based solely on automated processing — including profiling — which produces legal effects concerning you
            or similarly significantly affects you.
          </li>
          <li style={bodyStyle}>
            <strong>Right to withdraw consent:</strong> you may withdraw any consent given to us at any time, with
            effect for the future.
          </li>
          <li style={bodyStyle}>
            <strong>Right to lodge a complaint with a supervisory authority:</strong> without prejudice to any other
            administrative or judicial remedy, you have the right to lodge a complaint with a data protection
            supervisory authority, in particular in the member state of your habitual residence, place of work, or
            place of the alleged infringement.
          </li>
        </ul>
        <p style={bodyStyle}>To exercise these rights, you may contact us at any time using the contact details given in section 2.</p>
      </Section>

      <Section title="10. Google Analytics (GA4)">
        <p style={bodyStyle}>
          We use Google Analytics 4 (GA4) on our website, a web analytics service provided by Google Ireland
          Limited, Gordon House, Barrow Street, Dublin, D04 E5W5, Ireland. GA4 collects, among other things, data on
          which website a data subject came from (referrer), which subpages were accessed, and how long they were
          viewed, in order to optimize our website.
        </p>
        <p style={bodyStyle}>
          GA4 truncates and anonymizes IP addresses by default and without any separate configuration, before they
          are stored — unlike older versions of Google Analytics, no additional flag is required for this. GA4 is
          only loaded after the data subject has given consent via our cookie-consent banner.
        </p>
        <p style={bodyStyle}>
          The data subject can object to collection by GA4 by declining in the consent banner, or by installing the
          browser add-on provided by Google at{" "}
          <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer">
            https://tools.google.com/dlpage/gaoptout
          </a>
          . Further information on Google's privacy policy is available at{" "}
          <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">
            https://policies.google.com/privacy
          </a>
          .
        </p>
      </Section>

      <Section title="11. Cloudflare Turnstile">
        <p style={bodyStyle}>
          We use the Cloudflare Turnstile service on our website to protect forms (donations, contact) from
          automated access (bots) — a privacy-friendly alternative to classic CAPTCHAs that generally does not
          require solving a puzzle.
        </p>
        <p style={bodyStyle}>
          The operating company is Cloudflare, Inc., 101 Townsend St., San Francisco, CA 94107, USA; for users in
          the European Economic Area, Cloudflare Germany GmbH, Rosental 7, c/o Mindspace, 80331 Munich, is
          additionally named as a contact. When Turnstile is embedded, data such as IP address, browser and
          operating system information, the subpage accessed, date/time, and interaction data from the verification
          process may be transmitted to Cloudflare; this data may be stored in a cookie.
        </p>
        <p style={bodyStyle}>
          Processing is based on Art. 6(1)(f) GDPR — our legitimate interest is protecting our forms from abuse and
          spam. Transfer to the USA is based on the Standard Contractual Clauses approved by the European
          Commission; Cloudflare has also joined the EU-U.S. Data Privacy Framework. Cloudflare's privacy policy is
          available at{" "}
          <a href="https://www.cloudflare.com/de-de/privacypolicy/" target="_blank" rel="noopener noreferrer">
            https://www.cloudflare.com/de-de/privacypolicy/
          </a>
          , and the Turnstile Privacy Addendum at{" "}
          <a href="https://www.cloudflare.com/turnstile-privacy-policy/" target="_blank" rel="noopener noreferrer">
            https://www.cloudflare.com/turnstile-privacy-policy/
          </a>
          .
        </p>
      </Section>

      <Section title="12. PayPal as a payment method">
        <p style={bodyStyle}>
          We offer PayPal as a payment option for donations. The European operating company is PayPal (Europe)
          S.à.r.l. et Cie, S.C.A., 22-24 Boulevard Royal, L-2449 Luxembourg. If the data subject selects PayPal as
          the payment method, the data necessary for payment processing (e.g., name, address, email address, IP
          address) is automatically transmitted to PayPal. By selecting this payment option, the data subject
          consents to this transmission.
        </p>
        <p style={bodyStyle}>
          The transmission serves payment processing and fraud prevention. Withdrawing consent with PayPal does not
          affect data already processed for the contractual payment transaction. PayPal's privacy policy is
          available at{" "}
          <a href="https://www.paypal.com/de/webapps/mpp/ua/privacy-full" target="_blank" rel="noopener noreferrer">
            https://www.paypal.com/de/webapps/mpp/ua/privacy-full
          </a>
          .
        </p>
      </Section>

      <Section title="13. Azure Communication Services (email delivery)">
        <p style={bodyStyle}>
          We send automated emails (donation confirmation, donation receipt, payment reminder, forwarding of contact
          form inquiries) via Azure Communication Services, a service of Microsoft Ireland Operations Ltd., One
          Microsoft Place, South County Business Park, Leopardstown, Dublin 18, Ireland. This processes the data
          necessary for the respective email (including email address, name, and the subject/content of the
          relevant message).
        </p>
        <p style={bodyStyle}>
          The legal basis is Art. 6(1)(b) GDPR (fulfillment of the donation/contractual relationship) or Art.
          6(1)(f) GDPR (legitimate interest in responding to contact inquiries). A data processing agreement is in
          place with Microsoft. Further information on Microsoft Azure's privacy policy is available at{" "}
          <a href="https://privacy.microsoft.com/de-de/privacystatement" target="_blank" rel="noopener noreferrer">
            https://privacy.microsoft.com/de-de/privacystatement
          </a>
          .
        </p>
      </Section>

      <Section title="14. Storage of donation and contact data">
        <p style={bodyStyle}>
          To process donations (including donation receipts) and handle contact inquiries, we store personal data in
          a Microsoft Azure database (Cosmos DB), and generated donation receipts as PDFs in a Microsoft Azure
          storage account (Blob Storage). A data processing agreement with Microsoft applies here too; data is
          stored only as long as necessary to process the donation and to satisfy statutory retention obligations
          (in particular tax-related retention periods for donation receipts).
        </p>
      </Section>

      <Section title="15. Legal basis for processing">
        <p style={bodyStyle}>
          Where we obtain consent for a specific processing purpose, Art. 6(1)(a) GDPR serves as the legal basis for
          our processing. Where processing is necessary for the performance of a contract to which the data subject
          is party (e.g., processing a donation), the processing is based on Art. 6(1)(b) GDPR; the same applies to
          processing required to carry out pre-contractual measures. Where we are subject to a legal obligation that
          requires processing (e.g., tax retention obligations), processing is based on Art. 6(1)(c) GDPR.
          Otherwise, processing may be based on Art. 6(1)(f) GDPR, provided it is necessary to safeguard a legitimate
          interest and the data subject's interests, fundamental rights, and freedoms do not override it.
        </p>
      </Section>

      <Section title="16. Legitimate interests pursued in the processing">
        <p style={bodyStyle}>
          Where processing is based on Art. 6(1)(f) GDPR, our legitimate interest lies in fulfilling our non-profit
          purpose — running and safeguarding our LARP events, processing donations, and providing our website
          securely and reliably.
        </p>
      </Section>

      <Section title="17. Duration of storage">
        <p style={bodyStyle}>
          The criterion for the duration of storage of personal data is the applicable statutory retention period
          (in particular commercial and tax law retention periods for donation receipts). Once the period expires,
          the corresponding data is routinely erased, provided it is no longer needed for the performance or
          initiation of a contract.
        </p>
      </Section>

      <Section title="18. Provision of personal data">
        <p style={bodyStyle}>
          We advise that the provision of personal data is partly required by law (e.g., tax regulations for
          donation receipts) or may arise from contractual arrangements. Without certain data (e.g., a valid email
          address), a donation, newsletter registration, or the handling of a contact inquiry may not be possible.
        </p>
      </Section>

      <Section title="19. Automated decision-making">
        <p style={bodyStyle}>We do not use automated decision-making or profiling.</p>
      </Section>
    </div>
  );
}
